import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Clock, Package, MapPin, Scan } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { PickItemCard } from '../components/picking/PickItemCard';
import { CameraScanner } from '../components/scanner/CameraScanner';
import { usePickingStore } from '../stores/pickingStore';
import { useAppStore } from '../stores/appStore';
import { pickItemService } from '../services/supabase';
import type { PickItem } from '../types';

export const PickingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const { 
    currentPickList, 
    pickItems, 
    loadPickList, 
    updatePickItem, 
    completePickList,
    startScanning,
    stopScanning,
    scanning,
    scanResult,
    processScan,
    isLoading 
  } = usePickingStore();
  const { addNotification } = useAppStore();
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadPickList(id);
    }
  }, [id]);

  useEffect(() => {
    // Load pick items when pick list is loaded
    if (currentPickList) {
      const loadItems = async () => {
        const { data, error } = await pickItemService.getByPickList(currentPickList.id);
        if (data && !error) {
          const items: PickItem[] = data.map((item: any) => ({
            id: item.id,
            pickListId: item.pick_list_id,
            productSku: item.product_sku,
            productName: item.product_name,
            quantityRequired: item.quantity_required,
            quantityPicked: item.quantity_picked,
            binLocation: item.bin_location,
            status: item.status,
            barcode: item.barcode,
            notes: item.notes,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          }));
          // Update the store with the loaded items
          usePickingStore.setState({ pickItems: items });
          
          // Set default tab based on completion status
          const completedItems = items.filter(item => item.status === 'picked').length;
          const isComplete = completedItems === items.length && items.length > 0;
          setActiveTab(isComplete ? 'completed' : 'active');
        }
      };
      loadItems();
    }
  }, [currentPickList]);

  // Update tab when items change (when items are completed)
  useEffect(() => {
    if (pickItems.length > 0) {
      const completedItems = pickItems.filter(item => item.status === 'picked').length;
      const isComplete = completedItems === pickItems.length;
      
      // Only auto-switch to completed tab when all items are done
      if (isComplete && activeTab === 'active') {
        setActiveTab('completed');
      }
    }
  }, [pickItems]);

  const handleStartPicking = (itemId: string) => {
    // Auto-start picking is no longer needed since all items start in picking mode
    // This function is kept for compatibility but doesn't need to do anything
  };

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    // This function is now only called when completing items
    // Quantity changes are handled locally in PickItemCard
  };
  
  const handleCompleteItem = (itemId: string) => {
    const item = pickItems.find(item => item.id === itemId);
    if (!item) return;
    
    updatePickItem(itemId, { 
      quantityPicked: item.quantityRequired,
      status: 'picked'
    });
    
    addNotification({
      type: 'success',
      title: 'Item Completed',
      message: `${item.productName} completed successfully`,
      priority: 'low'
    });
  };

  const handleScanItem = (itemId: string) => {
    setSelectedItem(itemId);
    startScanning();
  };

  const handleScanResult = (barcode: string) => {
    processScan(barcode);
    stopScanning();
    
    if (scanResult?.success) {
      addNotification({
        type: 'success',
        title: 'Scan Successful',
        message: `Found ${scanResult.productName}`
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Scan Failed',
        message: scanResult?.message || 'Item not found'
      });
    }
  };

  const handleCompletePickList = async () => {
    if (!currentPickList) return;

    try {
      await completePickList();
      addNotification({
        type: 'success',
        title: 'Pick List Completed',
        message: 'All items have been picked successfully'
      });
      navigate('/');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to complete pick list'
      });
    }
  };

  if (isLoading) {
    return (
      <LoadingSpinner />
    );
  }

  if (!currentPickList) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Pick List Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The requested pick list could not be found.
          </p>
          <Button onClick={() => navigate('/')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const progress = pickItems.length > 0 ? 
    (pickItems.filter(item => item.status === 'picked').length / pickItems.length) * 100 : 0;
  const completedItems = pickItems.filter(item => item.status === 'picked').length;
  const activeItems = pickItems.filter(item => item.status !== 'picked');
  const completedPickItems = pickItems.filter(item => item.status === 'picked');
  const isComplete = completedItems === pickItems.length;

  return (
    <div className="p-4 pb-20 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            icon={<ArrowLeft className="w-4 h-4 text-saavi-600" />}
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentPickList.customerName}
            </h1>
            <p className="text-gray-600">
              {currentPickList.orderNumber}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Badge variant={currentPickList.status === 'completed' ? 'completed' : 'primary'}>
            {currentPickList.status === 'completed' ? 'completed' : currentPickList.status.replace('_', ' ')}
          </Badge>
          <Badge variant="warning">
            {currentPickList.priority}
          </Badge>
        </div>
      </motion.div>

      {/* Progress Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Progress Overview
            </h2>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-saavi-600" />
              <span className="text-sm text-gray-600">
                {currentPickList.estimatedTime || 30} min estimated
              </span>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Items Picked</span>
              <span>{completedItems}/{pickItems.length}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="bg-saavi-600 h-3 rounded-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600">{pickItems.length}</p>
              <p className="text-sm text-gray-600">Total Items</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{completedItems}</p>
              <p className="text-sm text-gray-600">Completed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{pickItems.length - completedItems}</p>
              <p className="text-sm text-gray-600">Remaining</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Pick Items */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        {/* Tab Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 w-full max-w-md">
            <button
              onClick={() => setActiveTab('active')}
              className={`
                flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-all
                ${activeTab === 'active'
                  ? 'bg-white text-green-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Package className={`w-4 h-4 ${activeTab === 'active' ? 'text-green-600' : ''}`} />
              <span className="text-base">Active Items</span>
              {activeItems.length > 0 && (
                <span className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${activeTab === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {activeItems.length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('completed')}
              className={`
                flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-all
                ${activeTab === 'completed'
                  ? 'bg-white text-saavi-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Check className="w-4 h-4" />
              <span className="text-base">Completed</span>
              {completedPickItems.length > 0 && (
                <span className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${activeTab === 'completed' 
                    ? 'bg-saavi-100 text-saavi-800' 
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {completedPickItems.length}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Quick Scan Button - Moved below tabs */}
        <div className="mb-4">
          <Button
            variant="primary"
            size="sm"
            onClick={() => startScanning()}
            className="w-full sm:w-auto"
            icon={<Scan className="w-4 h-4" />}
          >
            Quick Scan
          </Button>
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {activeTab === 'active' ? (
            activeItems.length > 0 ? (
              activeItems.map((item) => (
                <PickItemCard
                  key={item.id}
                  pickItem={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onCompleteItem={handleCompleteItem}
                  onStartPicking={handleStartPicking}
                  onScanItem={handleScanItem}
                />
              ))
            ) : (
              <Card className="text-center py-8">
               <Check className="w-12 h-12 text-saavi-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  All Items Picked!
                </h3>
                <p className="text-gray-600">
                  Great job! All items have been successfully picked.
                </p>
              </Card>
            )
          ) : (
            completedPickItems.length > 0 ? (
              completedPickItems.map((item) => (
                <PickItemCard
                  key={item.id}
                  pickItem={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onCompleteItem={handleCompleteItem}
                  onStartPicking={handleStartPicking}
                  onScanItem={handleScanItem}
                />
              ))
            ) : (
              <Card className="text-center py-8">
               <Package className="w-12 h-12 text-saavi-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Completed Items
                </h3>
                <p className="text-gray-600">
                  Completed items will appear here as you pick them.
                </p>
              </Card>
            )
          )}
        </div>
      </motion.div>

      {/* Complete Button */}
      {isComplete && currentPickList.status !== 'completed' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="text-center">
            <Check className="w-16 h-16 text-saavi-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              All Items Picked!
            </h3>
            <p className="text-gray-600 mb-4">
              You've successfully picked all items in this order.
            </p>
            <Button
              onClick={handleCompletePickList}
              size="lg"
              className="w-full"
              icon={<Check className="w-4 h-4" />}
            >
              Complete Pick List
            </Button>
          </Card>
        </motion.div>
      )}

      {/* Camera Scanner */}
      <CameraScanner
        isActive={scanning}
        onScan={handleScanResult}
        onClose={() => stopScanning()}
      />
    </div>
  );
};